// Sound events of a move: the footstep cycle of PM_Footsteps (bobCycle), the
// landing events of PM_CrashLand and the water events of PM_WaterEvents, as a
// pure function of the state after a move and its events. Like movetype.ts
// this only describes what a player hears; nothing here affects the movement.

import { EF_PRONE, ENTITYNUM_NONE, PMF_DUCKED, type PlayerState, type PmoveEvents, type UserCmd } from '../physics/pmove';
import { SURF_CARPET, SURF_GRASS, SURF_GRAVEL, SURF_METALSTEPS, SURF_NODAMAGE, SURF_NOSTEPS, SURF_ROOF, SURF_SNOW, SURF_SPLASH, SURF_WOOD } from '../surfaceflags';

/** footstep_t */
export const FOOTSTEP = {
  NORMAL: 0,
  METAL: 1,
  WOOD: 2,
  GRASS: 3,
  GRAVEL: 4,
  SPLASH: 5,
  ROOF: 6,
  SNOW: 7,
  CARPET: 8,
  /** SURF_NOSTEPS: no sound. */
  TOTAL: 9,
} as const;

/** Sound file prefix of every footstep type (sound/player/footsteps/<name>1..4.wav, <name>_jump.wav). */
export const FOOTSTEP_NAMES = ['stone', 'metal', 'wood', 'grass', 'gravel', 'water', 'roof', 'snow', 'carpet'] as const;

/** cgs.media sounds of the player (cg_main.c). */
export const PLAYER_SOUNDS = {
  landHurt: 'sound/player/land_hurt.wav',
  waterIn: 'sound/player/water_in.wav',
  waterOut: 'sound/player/water_out.wav',
  waterUnder: 'sound/player/water_un.wav',
} as const;

/** cgs.media.footsteps[surface][n] */
export const footstepFile = (surface: number, n: number): string => `sound/player/footsteps/${FOOTSTEP_NAMES[surface]}${n + 1}.wav`;
/** cgs.media.landSound[surface] */
export const landFile = (surface: number): string => `sound/player/footsteps/${FOOTSTEP_NAMES[surface]}_jump.wav`;

/** Every file of the player sounds (the server's sound bundle). */
export function playerSoundFiles(): string[] {
  const out: string[] = Object.values(PLAYER_SOUNDS);
  for (let s = 0; s < FOOTSTEP.TOTAL; s++) {
    for (let n = 0; n < 4; n++) out.push(footstepFile(s, n));
    out.push(landFile(s));
  }
  return out;
}

/** BG_FootstepForSurface */
export function footstepForSurface(surfaceFlags: number): number {
  if (surfaceFlags & SURF_NOSTEPS) return FOOTSTEP.TOTAL;
  if (surfaceFlags & SURF_METALSTEPS) return FOOTSTEP.METAL;
  if (surfaceFlags & SURF_WOOD) return FOOTSTEP.WOOD;
  if (surfaceFlags & SURF_GRASS) return FOOTSTEP.GRASS;
  if (surfaceFlags & SURF_GRAVEL) return FOOTSTEP.GRAVEL;
  if (surfaceFlags & SURF_ROOF) return FOOTSTEP.ROOF;
  if (surfaceFlags & SURF_SNOW) return FOOTSTEP.SNOW;
  if (surfaceFlags & SURF_CARPET) return FOOTSTEP.CARPET;
  if (surfaceFlags & SURF_SPLASH) return FOOTSTEP.SPLASH;
  return FOOTSTEP.NORMAL;
}

export type MoveSound =
  /** EV_FOOTSTEP (surface: FOOTSTEP value, never TOTAL) */
  | { kind: 'step'; surface: number }
  /** EV_FOOTSPLASH / EV_SWIM */
  | { kind: 'splash' }
  /** EV_FALL_*: the landing sound of the surface; hurt = also land_hurt.wav (delta > 38.75) */
  | { kind: 'land'; surface: number; hurt: boolean }
  /** EV_WATER_TOUCH / LEAVE / UNDER / CLEAR */
  | { kind: 'water'; event: 'touch' | 'leave' | 'under' | 'clear' };

/** What PM_Footsteps keeps between moves (ps->bobCycle). */
export interface StepState {
  bobCycle: number;
}

export const createStepState = (): StepState => ({ bobCycle: 0 });

/** The footstep part of PM_Footsteps: bob speed and whether running steps make a sound. */
export function stepCycle(ps: PlayerState, cmd: UserCmd): { bobmove: number; footstep: boolean } | null {
  // not trying to move: no cycle (the anim side is movetype.ts)
  if (!cmd.forwardmove && !cmd.rightmove) return null;
  // prone characters bob slower, ducked ones faster; neither plays footsteps
  if (ps.eFlags & EF_PRONE) return { bobmove: 0.2, footstep: false };
  if (ps.pmFlags & PMF_DUCKED) return { bobmove: 0.5, footstep: false };
  // there is no walk button: always the running branch
  return { bobmove: 0.4, footstep: true };
}

/**
 * Updates the footstep cycle after a move and returns the sounds it makes, in
 * the order pmove adds the events (landing, footsteps, water).
 */
export function updateMoveSounds(st: StepState, ps: PlayerState, cmd: UserCmd, events: PmoveEvents): MoveSound[] {
  const out: MoveSound[] = [];
  if (ps.pmType === 'noclip') return out;

  // PM_CrashLand: only when not on a SURF_NODAMAGE surface (bounce pads)
  if (events.landDelta >= 1) {
    if (!(events.landSurfaceFlags & SURF_NODAMAGE)) {
      const surface = footstepForSurface(events.landSurfaceFlags);
      if (surface !== FOOTSTEP.TOTAL) {
        // EV_FALL_SHORT and the damage events play the landing sound, a soft landing a footstep
        if (events.landDelta > 7) out.push({ kind: 'land', surface, hurt: events.landDelta > 38.75 });
        else out.push({ kind: 'step', surface });
      } else if (events.landDelta > 38.75) out.push({ kind: 'land', surface, hurt: true });
    }
    // start footstep cycle over
    st.bobCycle = 0;
  }

  // PM_Footsteps (swimming and the air make no steps)
  const xyspeed = Math.hypot(ps.velocity[0], ps.velocity[1]);
  if (events.waterlevel <= 2 && ps.groundEntityNum !== ENTITYNUM_NONE) {
    if (!cmd.forwardmove && !cmd.rightmove) {
      if (xyspeed < 5) st.bobCycle = 0; // start at beginning of cycle again
    } else {
      const cycle = stepCycle(ps, cmd)!;
      const old = st.bobCycle;
      st.bobCycle = Math.trunc(old + cycle.bobmove * events.msec) & 255;
      // a step on every half cycle
      if (((old + 64) ^ (st.bobCycle + 64)) & 128) {
        if (events.waterlevel === 0) {
          const surface = footstepForSurface(events.groundSurfaceFlags);
          if (cycle.footstep && surface !== FOOTSTEP.TOTAL) out.push({ kind: 'step', surface });
        } else out.push({ kind: 'splash' });
      }
    }
  }

  // PM_WaterEvents
  const prev = events.previousWaterlevel;
  const now = events.waterlevel;
  if (!prev && now) out.push({ kind: 'water', event: 'touch' });
  if (prev && !now) out.push({ kind: 'water', event: 'leave' });
  if (prev !== 3 && now === 3) out.push({ kind: 'water', event: 'under' });
  if (prev === 3 && now !== 3) out.push({ kind: 'water', event: 'clear' });
  return out;
}
